import React, { forwardRef, useRef } from 'react';
import classnames from 'classnames';
import { getSysFieldid } from '@platform/api';
import ColGroup from './ColGroup';

export default forwardRef(function TableHeader(props, ref) {
    /**
     * —————————————————————————————————————— hooks —————————————————————————————————————— 
     */
    let startX = useRef(0),
        startWidth = useRef(0);
    /**
     * —————————————————————————————————————— render —————————————————————————————————————— 
     */
    let { 
        rowHeight, 
        columnInfo, 
        isFixLeft, 
        isFixRight, 
        onColumnResize, 
        hideHeader,
        scrollBarSize, 
        hasVerticalScrollBar,
        bodyWidth,
        fixedTop,
        cellProps: getCellProps,
        rows: tableRows,
    } = props;

    !hasVerticalScrollBar && (scrollBarSize = 0);

    if (hideHeader) {
        return <div 
            ref={ref}
            className="table-wrapper table-next-header-wrapper" 
        />;
    }

    let { 
        flatColumns,
        left: { columns: leftColumns, offsets: leftOffsets }, 
        right: { offsets: rightOffsets }, 
        middle: { columns: middleColumns },
        leaf: { columnsWidth, columnsWidthMap, columns: leafColumns },
        headerRowsCount,
    } = columnInfo;

    let leftCount = leftColumns.length,
        middleCount = middleColumns.length;

    let rows = [...new Array(headerRowsCount)].map(() => []);
    flatColumns.forEach((column, index) => {
        let { height = 0, depth = 0, key, title, childLeafNodes, range, visible = true, itemtype, isLeaf, resizable } = column;

        let style = { height: rowHeight }, className = '';

        if (index < leftCount) {
            // 左侧固定列
            style = { 
                ...style,
                left: leftOffsets[index],
                // position: sticky时，右列会覆盖左列的拖拽条，故让左列index大右列1
            };
            className = classnames(
                className, 
                'fixed-left', 
                { 
                    'is-fixing': isFixLeft,
                    'last-left-column': index === leftColumns.length - 1,
                },
                'sticky',
            );
        } else if (index >= leftCount + middleCount) {
            // 右侧固定列
            style = { 
                ...style,
                right: rightOffsets[index - leftCount - middleCount] + scrollBarSize,
            };
            className = classnames(
                className, 
                'fixed-right', 
                { 
                    'is-fixing': isFixRight,
                    'first-right-column': index === leftCount + middleCount,
                    'last-right-column': index === flatColumns.length - 1,
                },
                'sticky',
            );
        }

        if (visible) {
            let rowIndex = 0 - headerRowsCount + depth;
            let cellProps = getCellProps?.({ row: { key: rowIndex }, rowIndex, rows: tableRows }, { col: column, columns: leafColumns, colIndex: column.leafIndex }) ?? {};
            rows[depth].push(<th 
                style={style}
                key={key} 
                colKey={key}
                colSpan={childLeafNodes?.length || 1} 
                rowSpan={isLeaf ? headerRowsCount - depth - height : 1}
                fieldid={getSysFieldid(key)}
                {...cellProps}
                className={classnames(
                    className,
                    cellProps.className,
                )}
            >
                <div className={`table-next-header-cell header-type-${itemtype}`}>{title}</div>
                {resizable && renderDragHandler(range)}
            </th>);
        }
    });

    // 这里需要计算出多表头内容是否占满，给个标识，方便去除last-child的边框
    let trClass = {};
    for (let i = 0; i < rows.length; i++) {
        if (rows[i].length) {
            trClass[i] = 'full-tr';
            if (i + rows[i][rows[i].length - 1].props.rowSpan === headerRowsCount) {
                break;
            }
        }
    }

    return <>
        <div 
            ref={ref}
            className={classnames(
                "table-wrapper",
                "table-next-header-wrapper", 
                "u-table-header", /* 最后一个是自动化标记 */
                { 'fix-top': fixedTop },
            )} 
            style={{ width: fixedTop ? bodyWidth : 'auto' }}
        >
            {/* <div
                ref={ref}
                className={classnames(
                    "table-wrapper",
                )} 
            > */}
            <table className="table-next-header">
                {bodyWidth ? <ColGroup {...props}></ColGroup> : null}
                {/* eslint-disable-next-line react/jsx-key */}
                <thead>{rows.map((ths, index) => <tr className={trClass[index]}>{bodyWidth ? ths : <th style={{ height: rowHeight }}></th>}</tr>)}</thead>
                {/* 有bodyWidth渲染真正的表头，不然先占位，避免blankcol从无到有引起的抖动问题 */}
            </table>
            {/* </div> */}
            {renderScrollBarBlank()}
        </div>
        {fixedTop && <div style={{ 
            height: ref.current.offsetHeight,
            width: bodyWidth,
        }}></div>}
    </>;

    /**
     * —————————————————————————————————————— functions —————————————————————————————————————— 
     */

    function renderDragHandler(range) {
        return <div 
            className="column-resize-handler"
            onMouseDown={e => {
                e.stopPropagation();
                let lastCursor = document.body.style.cursor;
                document.body.style.cursor = 'col-resize';
                
                startX.current = e.screenX;
                startWidth.current = columnsWidth.slice(...range);
    
                let dragColumns = leafColumns.slice(...range);

                columnsWidthMap = { ...columnsWidthMap };

                function resize(e) {
                    e.stopPropagation();
                    e.preventDefault();
                    // 每一列平分宽度
                    let offset = (e.screenX - startX.current) / startWidth.current.length;
                    let nextRangeWidth = startWidth.current.map(e => Math.max(60, e + offset));
                    
                    columnsWidthMap = dragColumns.reduce((columnsWidthMap, column, index) => {
                        columnsWidthMap[column.key] = nextRangeWidth[index];
                        return columnsWidthMap;
                    }, columnsWidthMap);

                    onColumnResize?.(columnsWidthMap);
                }

                const finished = () => {
                    document.body.style.cursor = lastCursor;
                    window.removeEventListener('mousemove', resize);
                    window.removeEventListener('mouseup', finished);
                };

                window.addEventListener('mousemove', resize);
                window.addEventListener('mouseup', finished);
            }}
        ></div>;
    }

    function renderScrollBarBlank() {
        return hasVerticalScrollBar && <div className="scroll-bar-blank" style={{ width: scrollBarSize }}></div>;
    }
});